import { streamingStepId } from './step-ids';
import { patchLiveStepTimings } from './stores/live-trail-timings';

import type { StepTimings } from './types';

interface PendingStepTiming {
  startedAt: number;
  completedAt?: number;
  gapBeforeMs?: number;
}

export interface StepTimingState {
  pendingStepTimings: Map<string, PendingStepTiming>;
  textIdToStepKey: Map<string, string>;
  textPartCounter: number;
  lastEventAt?: number;
}

export function createStepTimingState(initialLastEventAt?: number): StepTimingState {
  return {
    pendingStepTimings: new Map(),
    textIdToStepKey: new Map(),
    textPartCounter: 0,
    lastEventAt: initialLastEventAt,
  };
}

export interface StepTimingChunk {
  type: string;
  toolCallId?: string;
  id?: string;
  preliminary?: boolean;
}

/**
 * Narrow an unknown chunk to a StepTimingChunk-shaped object. Only `type`
 * is required (it's the discriminator). The optional fields are validated
 * lazily — reducer branches read them by name, so an extra property like
 * `someOtherField` on the chunk is harmless.
 */
export function isStepTimingChunk(chunk: unknown): chunk is StepTimingChunk {
  if (!chunk || typeof chunk !== 'object') return false;
  const c = chunk as Record<string, unknown>;
  if (typeof c.type !== 'string') return false;
  if (c.toolCallId !== undefined && typeof c.toolCallId !== 'string') return false;
  if (c.id !== undefined && typeof c.id !== 'string') return false;
  if (c.preliminary !== undefined && typeof c.preliminary !== 'boolean') return false;
  return true;
}

interface ApplyChunkArgs {
  chunk: StepTimingChunk;
  now: number;
  state: StepTimingState;
  threadId: string | null | undefined;
  /** When true, stamp `gapBeforeMs` on each new step from the running `lastEventAt`. */
  trackGapBefore: boolean;
}

function buildTimingPatch(entry: PendingStepTiming): {
  startedAt: number;
  completedAt?: number;
  gapBeforeMs?: number;
} {
  return {
    startedAt: entry.startedAt,
    ...(entry.completedAt !== undefined && { completedAt: entry.completedAt }),
    ...(entry.gapBeforeMs !== undefined && { gapBeforeMs: entry.gapBeforeMs }),
  };
}

/**
 * Updates `state` for a single UIMessageChunk, then patches the live store.
 * Mirrors the chunk taxonomy: `tool-input-start` opens a tool row;
 * `tool-output-available|error|denied` and `tool-input-error` close it
 * (skipping preliminary streaming-progress outputs); `text-start`/`text-end`
 * open and close text rows under a counter-derived step id.
 */
export function applyChunkToStepTimings({
  chunk,
  now,
  state,
  threadId,
  trackGapBefore,
}: ApplyChunkArgs): void {
  const c = chunk;
  const gapBeforeMs =
    trackGapBefore && state.lastEventAt !== undefined ? Math.max(0, now - state.lastEventAt) : 0;

  let mutatedKey: string | undefined;

  if (c.type === 'tool-input-start' && c.toolCallId) {
    if (!state.pendingStepTimings.has(c.toolCallId)) {
      state.pendingStepTimings.set(c.toolCallId, {
        startedAt: now,
        ...(trackGapBefore && { gapBeforeMs }),
      });
      state.lastEventAt = now;
      mutatedKey = c.toolCallId;
    }
  } else if (
    (c.type === 'tool-output-available' ||
      c.type === 'tool-output-error' ||
      c.type === 'tool-input-error' ||
      c.type === 'tool-output-denied') &&
    c.toolCallId
  ) {
    // Preliminary outputs ship while the tool keeps running — skip so the
    // close lands on the final non-preliminary output.
    const isPreliminary = c.type === 'tool-output-available' && c.preliminary === true;
    if (!isPreliminary) {
      // Synchronous tools may emit `tool-output-*` without `tool-input-start`.
      let entry = state.pendingStepTimings.get(c.toolCallId);
      if (!entry) {
        entry = { startedAt: now, ...(trackGapBefore && { gapBeforeMs }) };
        state.pendingStepTimings.set(c.toolCallId, entry);
      }
      if (entry.completedAt === undefined) {
        entry.completedAt = now;
        state.lastEventAt = now;
        mutatedKey = c.toolCallId;
      }
    }
  } else if (c.type === 'text-start' && c.id) {
    const stepKey = streamingStepId(state.textPartCounter++);
    state.textIdToStepKey.set(c.id, stepKey);
    if (!state.pendingStepTimings.has(stepKey)) {
      state.pendingStepTimings.set(stepKey, {
        startedAt: now,
        ...(trackGapBefore && { gapBeforeMs }),
      });
      state.lastEventAt = now;
      mutatedKey = stepKey;
    }
  } else if (c.type === 'text-end' && c.id) {
    const stepKey = state.textIdToStepKey.get(c.id);
    if (stepKey) {
      const entry = state.pendingStepTimings.get(stepKey);
      if (entry && entry.completedAt === undefined) {
        entry.completedAt = now;
        state.lastEventAt = now;
        mutatedKey = stepKey;
      }
    }
  }

  if (mutatedKey && threadId) {
    const entry = state.pendingStepTimings.get(mutatedKey)!;
    const patchValue = buildTimingPatch(entry);
    patchLiveStepTimings({
      threadId,
      patch: prev => ({ ...prev, [mutatedKey]: patchValue }),
    });
  }
}

/**
 * Snapshots `state` into a final `StepTimings` map, filling synthetic
 * `completedAt`s for any entry whose close chunk never arrived (e.g. a
 * stream cut mid-text-delta with no `text-end`/`finish`). Patches the live
 * store with the same values so consumers see non-zero durations
 * immediately, ahead of the persisted `step_timings` write.
 */
export function finalizeStepTimings({
  state,
  finishAt,
  threadId,
}: {
  state: StepTimingState;
  finishAt: number;
  threadId: string | null | undefined;
}): StepTimings {
  const stepTimings: StepTimings = {};
  for (const [k, v] of state.pendingStepTimings) {
    const completedAt = v.completedAt ?? finishAt;
    stepTimings[k] = {
      startedAt: v.startedAt,
      completedAt,
      ...(v.gapBeforeMs !== undefined && { gapBeforeMs: v.gapBeforeMs }),
    };
  }
  if (threadId) {
    patchLiveStepTimings({
      threadId,
      patch: prev => ({ ...prev, ...stepTimings }),
    });
  }
  return stepTimings;
}
